// Money formatting for the wallet pages.
//
// Returns a STRING, unlike the customer app's `formatPrices` which returns JSX. One function then
// serves tables, modals, CSV export and PDF alike — a JSX-returning helper cannot be written into a
// CSV cell, which is how a codebase ends up with two subtly different money formatters.
//
// Locale is pinned to en-US rather than left to the browser. The panel has no i18n, and an operator
// on an ar-SA machine would otherwise see Arabic-Indic digits (٢٥٠٫٠٠) in a table sitting next to
// Latin ones from elsewhere in the app — and, worse, a CSV whose numbers a spreadsheet cannot parse.
//
// Colour is NOT applied here. A formatter that decided its own colour could not be reused in a
// context with a different background; the components choose.
//
// Scope note: use these in the wallet pages and in rows you are already touching. Do NOT sweep the
// ~200 existing inline `.toFixed(2)` sites — that is a separate change with its own review.

const LOCALE = "en-US";
const CURRENCY = "SAR";

// Non-breaking space before the currency, so an amount never wraps onto two lines inside a narrow
// table cell — "250.00" on one line and "SAR" on the next reads as a different number at a glance.
const NBSP = " ";

const toNumber = (value) => {
  // DECIMAL columns arrive from the API as numbers thanks to the model getters, but a raw query or
  // a CSV round-trip can still deliver a string. Coerce rather than render "NaN" at an operator.
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * `1234.5` -> `"1,234.50 SAR"`. Blank/invalid -> `"—"`.
 *
 * @param {number|string|null} value
 * @param {{ withCurrency?: boolean, dash?: string }} [opts]
 */
export const formatCurrency = (value, opts = {}) => {
  const { withCurrency = true, dash = "—" } = opts;
  const n = toNumber(value);
  if (n === null) return dash;

  const formatted = n.toLocaleString(LOCALE, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return withCurrency ? `${formatted}${NBSP}${CURRENCY}` : formatted;
};

/**
 * Ledger deltas, where the sign carries meaning: `-25` -> `"−25.00 SAR"`, `25` -> `"+25.00 SAR"`.
 *
 * Uses a real minus sign (U+2212), not a hyphen: at small table sizes a hyphen is easy to miss, and
 * mistaking a debit for a credit is the specific misreading this view must not cause.
 */
export const formatSignedCurrency = (value, opts = {}) => {
  const n = toNumber(value);
  if (n === null) return opts.dash ?? "—";
  const sign = n < 0 ? "−" : "+";
  return `${sign}${formatCurrency(Math.abs(n), opts)}`;
};

/** `0.1` -> `"10%"`; pass already-scaled percentages with `alreadyScaled`. */
export const formatPercent = (value, { alreadyScaled = true, dash = "—" } = {}) => {
  const n = toNumber(value);
  if (n === null) return dash;
  const scaled = alreadyScaled ? n : n * 100;
  // Trim a trailing ".0" so "10%" does not render as "10.0%".
  return `${scaled % 1 === 0 ? scaled : scaled.toFixed(1)}%`;
};

/** Consistent date rendering, so the wallet pages do not each re-implement it. */
export const formatDateTime = (value, { dash = "—" } = {}) => {
  if (!value) return dash;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return dash;
  return d.toLocaleString(LOCALE, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default formatCurrency;
